/**
 * API du Module Structure de Base - Modularité Absolue
 * 
 * Interface claire et API bien définie selon cahier des charges
 * Isolation totale des erreurs - Aucun impact sur autres modules
 */

import {
  ParametresStructureBase,
  StructureBase,
  GeometrieForme,
  Montant,
  StructureBaseAPIResponse,
  TypeMateriauGlobal,
  TypeForme
} from '../types';

/**
 * Gestionnaire d'API centralisé pour isolation des modules
 */
export interface ModuleAPI {
  cleanup(): void;
}

export interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  moduleId: string;
}

export class ModuleAPIManager {
  private modules = new Map<string, ModuleAPI>();
  private baseUrl = process.env.REACT_APP_API_URL || '';

  registerModule(moduleId: string, api: ModuleAPI): void {
    this.modules.set(moduleId, api);
  }

  unregisterModule(moduleId: string): void {
    const api = this.modules.get(moduleId);
    if (api) {
      api.cleanup();
      this.modules.delete(moduleId);
    }
  }

  isModuleRegistered(moduleId: string): boolean {
    return this.modules.has(moduleId);
  }

  getBaseUrl(): string {
    return this.baseUrl;
  }
}

// Instance singleton
export const apiManager = new ModuleAPIManager();

/**
 * API principale du module Structure de Base
 * Respecte la modularité absolue
 */
export class StructureBaseModuleAPI implements ModuleAPI {
  private moduleId = 'structure-base';
  private baseUrl = `${apiManager.getBaseUrl()}/api/structure-base`;

  /**
   * Génération des formes 3D (Tâche 1.1)
   */
  async genererForme(parametres: ParametresStructureBase): Promise<APIResponse<GeometrieForme>> {
    try {
      const response = await fetch(`${this.baseUrl}/formes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(parametres)
      });

      if (!response.ok) {
        throw new Error(`Erreur API: ${response.status}`);
      }

      const data: StructureBaseAPIResponse = await response.json();
      
      if (!data.success || data.error) {
        return {
          success: false,
          error: data.error || 'Erreur génération forme',
          moduleId: this.moduleId
        };
      }

      return {
        success: true,
        data: data.geometrie!,
        moduleId: this.moduleId
      };

    } catch (error) {
      return {
        success: false,
        error: `Erreur module structure-base: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
        moduleId: this.moduleId
      };
    }
  }

  /**
   * Génération des poutres (Tâche 1.3)
   */
  async genererPoutres(formeBase: GeometrieForme, montants?: any[]): Promise<APIResponse<any>> {
    try {
      const response = await fetch(`${this.baseUrl}/poutres`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ formeBase, montants })
      });

      if (!response.ok) {
        throw new Error(`Erreur API poutres: ${response.status}`);
      }

      const data: StructureBaseAPIResponse = await response.json();
      
      if (!data.success || data.error) {
        return {
          success: false,
          error: data.error || 'Erreur génération poutres',
          moduleId: this.moduleId
        };
      }

      return {
        success: true,
        data: data.poutres!,
        moduleId: this.moduleId
      };

    } catch (error) {
      return {
        success: false,
        error: `Erreur poutres: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
        moduleId: this.moduleId
      };
    }
  }

  /**
   * Génération des montants (Tâche 1.4)
   */
  async genererMontants(
    formeBase: GeometrieForme,
    nombreMontants: number
  ): Promise<APIResponse<{ montants: Montant[]; nombreMontants: number; espacementOptimal?: string }>> {
    try {
      const response = await fetch(`${this.baseUrl}/montants`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ formeBase, nombreMontants })
      });

      if (!response.ok) {
        throw new Error(`Erreur API montants: ${response.status}`);
      }

      const data: StructureBaseAPIResponse = await response.json();
      
      if (!data.success || data.error) {
        return {
          success: false,
          error: data.error || 'Erreur génération montants',
          moduleId: this.moduleId
        };
      }

      return {
        success: true,
        data: {
          montants: data.montants!,
          nombreMontants: data.nombreMontants || nombreMontants,
          espacementOptimal: data.espacementOptimal
        },
        moduleId: this.moduleId
      };

    } catch (error) {
      return {
        success: false,
        error: `Erreur montants: ${error instanceof Error ? error.message : 'Erreur inconnue'}`,
        moduleId: this.moduleId
      };
    }
  }





  /**
   * Obtenir la configuration par défaut
   */
  getConfigurationParDefaut(): ParametresStructureBase {
    return {
      type: TypeForme.RECTANGLE,
      largeur: 4,
      longueur: 6,
      hauteur: 2.5,
      nombreCotes: 6,
      materiau: TypeMateriauGlobal.FER_A_BETON,
      epaisseur: 20,
      couleur: '#8B4513'
    };
  }

  /**
   * Nettoyage des ressources
   */
  cleanup(): void {
    // Nettoyage des ressources si nécessaire
  }
}

// Instance singleton
export const structureBaseAPI = new StructureBaseModuleAPI();

/**
 * Fonction utilitaire pour appeler l'API structure de base
 * Isolation d'erreur garantie
 */
export async function callStructureBaseAPI(
  parametres: ParametresStructureBase,
  nombreMontants: number = 4
): Promise<StructureBase> {
  try {
    // Enregistrement du module si pas déjà fait
    if (!apiManager.isModuleRegistered('structure-base')) {
      apiManager.registerModule('structure-base', structureBaseAPI);
    }

    // Génération de la forme
    const formeResult = await structureBaseAPI.genererForme(parametres);
    if (!formeResult.success) {
      return {
        forme: {} as GeometrieForme,
        poutres: { poutreBasse: {} as any, poutreHaute: {} as any },
        montants: [],
        parametres,
        nombreMontants: 0,
        error: formeResult.error
      };
    }

    // Vérification que la forme a été générée avec succès
    if (!formeResult.data) {
      return {
        forme: {} as GeometrieForme,
        poutres: { poutreBasse: {} as any, poutreHaute: {} as any },
        montants: [],
        parametres,
        nombreMontants: 0,
        error: 'Erreur lors de la génération de la forme'
      };
    }

    // Génération des montants d'abord
    const montantsResult = await structureBaseAPI.genererMontants(formeResult.data, nombreMontants);
    if (!montantsResult.success || !montantsResult.data) {
      return {
        forme: formeResult.data,
        poutres: { poutreBasse: {} as any, poutreHaute: {} as any },
        montants: [],
        parametres,
        nombreMontants: 0,
        error: montantsResult.error || 'Erreur lors de la génération des montants'
      };
    }

    // Génération des poutres avec les montants pour les formes circulaires
    const poutresResult = await structureBaseAPI.genererPoutres(
      formeResult.data, 
      (formeResult.data.type === 'cercle' || formeResult.data.type === 'ovale') ? montantsResult.data.montants : undefined
    );
    if (!poutresResult.success || !poutresResult.data) {
      return {
        forme: formeResult.data,
        poutres: { poutreBasse: {} as any, poutreHaute: {} as any },
        montants: montantsResult.data.montants,
        parametres,
        nombreMontants: montantsResult.data.nombreMontants,
        error: poutresResult.error || 'Erreur lors de la génération des poutres'
      };
    }

    return {
      forme: formeResult.data,
      poutres: poutresResult.data,
      montants: montantsResult.data.montants,
      parametres,
      nombreMontants: montantsResult.data.nombreMontants
    };

  } catch (error) {
    return {
      forme: {} as GeometrieForme,
      poutres: { poutreBasse: {} as any, poutreHaute: {} as any },
      montants: [],
      parametres,
      nombreMontants: 0,
      error: `Erreur API structure de base: ${error instanceof Error ? error.message : 'Erreur inconnue'}`
    };
  }
}
